import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import BackArrowIcon from '@/components/Icons/BackArrowIcon';
import EditIcon from '@/components/Icons/EditIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import ManualSalaryAdjustmentModal from '@/components/Modal/ManualSalaryAdjustmentModal';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import useFilter from '@/hooks/useFilter';
import { IPageInfo } from '@/interfaces/common.model';
import { ISalaryResponse, ISalaryTableData } from '@/interfaces/salary.model';
import { RootState } from '@/redux/store';
import ExportDataHandler from '@/utility/CommonFunctions';
import {
  getFilterUrl,
  handleFormatAmount,
  handlePaySlipCalculation,
  showToastNotification,
} from '@/utility/helper';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
interface IPagination extends IPageInfo {
  count: number;
  searchQuery: string;
}
export interface IGenrateSalaryResponse {
  status: boolean;
  message: string;
  data: string;
}
interface IState {
  paySlipList: ISalaryTableData[];
}

const PayslipOfficeWise = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [selectedDocument, setSelectedDocument] = useState({
    url: '',
  });
  const [modal, setModal] = useState(false);
  const toggleModal = () => {
    setModal(!modal);
  };
  const filterDetails = useSelector(
    (state: RootState) => state.user.filtersStates,
  );
  const [pagination, setPagination] = useState<IPagination>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    searchQuery: '',
  });
  const [state, setState] = useState<IState>({
    paySlipList: [],
  });
  const [pageState, setPageStates] = useState({
    tableLoader: false,
    sortByDate: location?.state?.month,
  });
  const [adjustmentModal, setAdjustmentModal] = useState<{
    isOpen: boolean;
    userId: string;
  }>({
    isOpen: false,
    userId: '',
  });

  const openAdjustmentModal = (data: ISalaryTableData) => {
    setAdjustmentModal({ isOpen: true, userId: data?.id });
  };

  const closeAdjustmentModal = () => {
    setAdjustmentModal({ isOpen: false, userId: '' });
  };

  const getPaySlip = useCallback(async () => {
    try {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: true,
      }));
      const filter = {
        search: pagination?.searchQuery,
        pageNumber: pagination?.currentPage,
        pageSize: filterDetails?.pageSize,
        startDate: pageState?.sortByDate[0],
        endDate: pageState?.sortByDate[1],
        siteId: id,
      };
      const Url =
        API_URL +
        API_ENDPOINT?.getMonthlyUserSalary +
        '?' +
        getFilterUrl(filter);

      const res: ISalaryResponse = await callApi(Url, {
        method: 'GET',
      });

      if (res?.status === true) {
        const newData = res?.data?.userSalary?.map((item) => ({
          ...item,
          empNameWithImage: item?.name + '##' + item?.profilePhoto,
          basicSalary: handleFormatAmount(item?.['Basic Salary']) || '--',
          ...(() => {
            const { deductionAmount, netSalary } = handlePaySlipCalculation(
              item?.['Gross Salary'],
              item?.['deduction'],
            );
            return {
              grossSalary: handleFormatAmount(item?.['Gross Salary']) || '--',
              deductionAmount: handleFormatAmount(deductionAmount),
              netSalary: handleFormatAmount(netSalary),
            };
          })(),
        }));
        setState((pre) => ({
          ...pre,
          paySlipList: newData,
        }));
        setPagination((prev) => ({
          ...prev,
          count: res?.data?.pageInfo?.total ?? 0,
          totalPages: res?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: false,
      }));
    }
  }, [
    pagination?.searchQuery,
    pagination?.currentPage,
    filterDetails?.pageSize,
    pageState?.sortByDate,
    id,
  ]);

  useEffect(() => {
    getPaySlip();
  }, [getPaySlip]);

  const tableHeading: ITableColumn<ISalaryTableData>[] = [
    { value: 'employeeCode', label: 'Employee Code' },
    { value: 'name', label: 'Employee' },
    { value: 'basicSalary', label: 'Basic Pay' },
    { value: 'grossSalary', label: 'Gross Pay' },
    { value: 'deductionAmount', label: 'Deduction' },
    { value: 'netSalary', label: 'Net Pay' },
  ];

  const { handleSearchChange, handleChangePageSize, pageChangeHandler } =
    useFilter(pagination, setPagination);
  const handleExport = () => {
    const exportData = {
      exportDataType: 'salaryData',
      siteId: id,
      startDate: location?.state?.month[0],
      endDate: location?.state?.month[1],
    };
    ExportDataHandler(exportData);
  };

  const HandleViewPayslip = async (data: ISalaryTableData) => {
    try {
      if (!data?.['Basic Salary']) {
        const [year, month] = pageState?.sortByDate?.[0]?.split('-') || [];
        const shortMonths = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const date = `${shortMonths[month - 1]} - ${year}`;
        showToastNotification(
          'danger',
          `${date} salary is not created for this user.`,
        );
        return;
      }
      setPageStates((pre) => ({
        ...pre,
        isLoadingGeneratePayslip: true,
      }));
      const API = `${API_URL}${API_ENDPOINT?.genereatePaySlip}userId=${data?.id}&startDate=${pageState?.sortByDate?.[0]}`;
      const response: IGenrateSalaryResponse = await callApi(API, {
        method: 'GET',
      });

      if (response?.status === true) {
        setSelectedDocument({ url: response?.data });
        setModal(true);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        isLoadingGeneratePayslip: false,
      }));
    }
  };

  const actionData = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view payslip',
      clickHandler: HandleViewPayslip,
    },
    {
      label: 'Adjust',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'manual salary adjustment',
      clickHandler: openAdjustmentModal,
    },
  ];

  return (
    <Layout>
      <div className="content">
        <div className="content-head mb-5 d-flex align-items-center justify-content-between">
          <div className="content-head mb-5 d-flex align-items-start justify-content-between">
            <div className="d-flex align-items-center justify-content-start gap-4">
              <button
                role="button"
                className="btn back-arrow"
                onClick={() => navigate(-1)}
              >
                <BackArrowIcon />
              </button>
              <h1 className="mb-0">{`Pay Slips - ${id ? location?.state?.data?.name : ''}`}</h1>
            </div>
          </div>
        </div>
        <CardLayout>
          <FilterComponent
            searchPlaceHolder="Search"
            handleSearch={handleSearchChange}
            handleExport={handleExport}
          />
          <CommonTable
            tableHeading={tableHeading}
            tableData={state?.paySlipList}
            ActionData={actionData}
            count={pagination?.count}
            pageSize={filterDetails?.pageSize}
            currentPage={pagination?.currentPage}
            totalPages={pagination?.totalPages}
            handleChangePageSize={handleChangePageSize}
            pageChangeHandler={pageChangeHandler}
          />
        </CardLayout>
      </div>
      <ViewDocumentModal
        isOpen={modal}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />

      <ManualSalaryAdjustmentModal
        isOpen={adjustmentModal.isOpen}
        userId={adjustmentModal.userId}
        siteId={id ?? ''}
        startDate={pageState?.sortByDate?.[0] ?? ''}
        onClose={closeAdjustmentModal}
        onSuccess={getPaySlip}
      />
    </Layout>
  );
};

export default PayslipOfficeWise;
